import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Alert,
} from '@mui/material';
import {
  Schedule,
  ArrowForward
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

// Set Italian locale
dayjs.locale('it');

const NextCourses = ({ courses = [], onCourseClick, isClickable = true }) => {
  const navigate = useNavigate();

  // Get upcoming courses from today to one week ahead
  const getUpcomingCourses = () => {
    if (!courses || !Array.isArray(courses)) return [];
    
    const today = dayjs().startOf('day');
    const oneWeekFromNow = dayjs().add(7, 'day').endOf('day');
    
    const upcomingCourses = [];
    
    courses.forEach(course => {
      if (course.dates && Array.isArray(course.dates)) {
        course.dates.forEach(courseDate => {
          const date = dayjs(courseDate);
          if (date.isAfter(today) && date.isBefore(oneWeekFromNow)) {
            upcomingCourses.push({
              ...course,
              courseDate: courseDate
            });
          }
        });
      }
    });
    
    // Sort by date and time
    return upcomingCourses.sort((a, b) => {
      const dateA = dayjs(a.courseDate);
      const dateB = dayjs(b.courseDate);
      return dateA.isBefore(dateB) ? -1 : 1;
    });
  };

  const upcomingCourses = getUpcomingCourses();

  const handleCourseClick = (courseId) => {
    if (!isClickable) return;
    
    if (onCourseClick) {
      onCourseClick(courseId);
    } else {
      navigate(`/teacher/course/${courseId}`);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Schedule sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          Prossimi Corsi
        </Typography>
      </Box>

      {upcomingCourses.length === 0 ? (
        <Alert severity="info">
          Nessun corso programmato per la prossima settimana
        </Alert>
      ) : (
        <List sx={{ p: 0 }}>
          {upcomingCourses.map((course, index) => (
            <React.Fragment key={`${course._id}-${course.courseDate}`}>
              <ListItem sx={{ p: 0, mb: 0.5 }}>
                <ListItemButton 
                  onClick={() => handleCourseClick(course._id)}
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 1,
                    ...(isClickable ? {
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        transform: 'scale(1.02)',
                        transition: 'all 0.2s'
                      }
                    } : {
                      cursor: 'default',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        transform: 'none'
                      }
                    })
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {course.name} {course.minLevel ? `(${course.minLevel}° anno)` : ''}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                      {course.venue} • {dayjs(course.courseDate).format('DD MMM YY')} {course.time || ''}
                    </Typography>
                  </Box>
                  {isClickable && <ArrowForward sx={{ fontSize: 16, color: 'primary.main' }} />}
                </ListItemButton>
              </ListItem>
              {index < upcomingCourses.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default NextCourses; 

NextCourses.propTypes = {
  courses: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string,
      dates: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])),
      venue: PropTypes.string,
      time: PropTypes.string,
      minLevel: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  onCourseClick: PropTypes.func,
  isClickable: PropTypes.bool,
};